import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import * as Handlebars from 'handlebars';

import { environment } from '@rapider/environments';
import {
  ProjectDataService,
  ProjectDataActions,
  ModelMutationDescriptor,
  selectProjectModelsList,
  resolveMutationModelTokens,
  clearSwrSessionCacheForEntities,
} from '@rapider/project-data';
import {
  ProjectChannelMessageControllerService,
  AiFunctionCallControllerService,
} from '@rapider/sdk';
import { BrowserStorageManagementService } from '@rapider/services';

import { AiChatActions } from '../state/ai-chat.actions';
import { selectMessages } from '../state/ai-chat.reducer';

import {
  AiAction,
  WorkflowPlan,
  WorkflowTask,
  buildPathWithParams,
  buildDataOperationEventPayload,
  generateTempId,
  parseAiResponseText,
  extractEntityData,
  extractAiActions,
  extractAiActionsUIConfig,
  buildDirectMessageFormDataPayload,
  extractWorkflowPlanUpdate,
  extractContextFromAiResponse,
  extractAiError,
  generateSampleContext,
  buildJsonataTransformationPrompt,
  extractJsonataExpression,
  executeJsonataTransformation,
  isServerAction,
  AI_CHAT_CONFIG,
} from '../ai-chat.config';

/**
 * @class WorkflowExecutionService
 * @description The core engine orchestrating the sequential execution of AI-generated Workflow Plans and Actions.
 * * **Summary of Responsibilities:**
 * 1. **Stateful Execution:** Manages the step-by-step execution of multi-task workflows, tracking status natively.
 * 2. **Context Memory & Variable Chaining:** Maintains a persistent, thread-isolated memory dictionary. It saves outputs
 * from one API action and injects them into subsequent actions by dynamically resolving Handlebars templates (`{{var}}`).
 * 3. **History Synchronization:** Fires intermediate and final execution results back to the `MessageController`,
 * ensuring that workflow progression is permanently logged in the chat history.
 * 4. **Human-In-The-Loop Execution:** Intelligently yields execution back to the UI after processing a single workflow step.
 * Allows users to manually execute endpoints, natively picking up context memory sequentially.
 */
@Injectable({ providedIn: 'root' })
export class WorkflowExecutionService {
  /** Injects the native Angular HttpClient for executing raw REST calls to the Rapider environment. */
  private readonly http = inject(HttpClient);

  /** Injects the global NgRx Store to dispatch UI state updates (loading, success, error flags). */
  private readonly store = inject(Store);

  /** Injects the ProjectDataService to securely route API calls to the user's generated backend workspace. */
  private readonly projectDataService = inject(ProjectDataService);

  /** Injects the SDK Controller to push AI execution artifacts into the persistent chat thread history. */
  private readonly messageController = inject(
    ProjectChannelMessageControllerService,
  );

  /** Injects the SDK Controller to communicate directly with backend AI orchestration agents. */
  private readonly aiFunctionController = inject(
    AiFunctionCallControllerService,
  );

  /** Injects the BrowserStorage service to persist memory context across page refreshes. */
  private readonly browserStorage = inject(BrowserStorageManagementService);

  /**
   * Persistent memory cache allowing disconnected manual UI clicks to share dynamic Handlebars context.
   * It is keyed by the `threadId` so multiple parallel chat conversations don't corrupt each other's data variables.
   * @type {Map<string, Record<string, any>>}
   */
  private persistentContextMemory = new Map<string, Record<string, any>>();

  /**
   * Safely retrieves or initializes a persistent context memory block for a given thread.
   * Tries to load from browser storage first to survive page refreshes.
   * * @param {string | null} threadId - The active chat thread ID.
   * @returns {Record<string, any>} The memory context dictionary used for Handlebars compilation.
   */
  private getThreadContext(threadId: string | null): Record<string, any> {
    const key = `workflow_context_${threadId || 'transient-thread'}`;

    // Attempt to recover from local storage to survive page reloads
    const storedContext = this.browserStorage.getItem<Record<string, any>>(key);
    if (storedContext) {
      this.persistentContextMemory.set(key, storedContext);
      return storedContext;
    }

    if (!this.persistentContextMemory.has(key)) {
      this.persistentContextMemory.set(key, {});
    }
    return this.persistentContextMemory.get(key)!;
  }

  /**
   * Saves the current context to both in-memory cache and browser storage.
   * @param {string | null} threadId - The active chat thread ID.
   * @param {Record<string, any>} context - The context object to store.
   */
  private saveThreadContext(
    threadId: string | null,
    context: Record<string, any>,
  ): void {
    const key = `workflow_context_${threadId || 'transient-thread'}`;
    this.persistentContextMemory.set(key, context);
    this.browserStorage.setItem(key, context);
  }

  /**
   * Clears a context from memory and storage, used when remapping transient threads.
   * @param {string | null} threadId - The thread ID to clear.
   */
  private clearThreadContext(threadId: string | null): void {
    const key = `workflow_context_${threadId || 'transient-thread'}`;
    this.persistentContextMemory.delete(key);
    this.browserStorage.removeItem(key);
  }

  /**
   * Orchestrates the execution of a multi-step Workflow Plan natively via the Chat History.
   * Implements a strict "Human-In-The-Loop" architecture, sequentially executing steps, yielding to the UI
   * if manual endpoint execution is required, and naturally progressing if automated.
   * * @param {WorkflowPlan} plan - The declarative workflow plan object containing an array of tasks.
   * @param {string} messageId - The UI chat message ID containing the original workflow plan card.
   * @param {string | null} activeThreadId - The active chat thread ID used for context mapping and backend logging.
   * @returns {Promise<void>} Resolves when the plan completes or yields back to the UI.
   */
  public async executeWorkflowPlanSequentially(
    plan: WorkflowPlan,
    messageId: string,
    activeThreadId: string | null,
  ): Promise<void> {
    // Isolate activeThreadId to a local variable to prevent ESLint no-param-reassign warnings during updates
    let currentThreadId = activeThreadId;

    // Retrieve the persistent context for this specific thread so previous executions are remembered
    let context = this.getThreadContext(currentThreadId);

    // Resolve the real message ID from the store to ensure updates are linked correctly, avoiding 'temp-' IDs
    const messages = this.store.selectSignal(selectMessages)();
    const actualMessage = messages.find(
      (m) =>
        m.id === messageId || m.workflowPlan?.originalMessageId === messageId,
    );
    const realMessageId = actualMessage?.id || messageId;

    // Initialize the main workflow plan UI card to a loading state
    this.store.dispatch(
      AiChatActions.setWorkflowExecutionState({
        messageId,
        executionState: { status: 'loading' },
      }),
    );

    let allTasksCompleted = true;

    // Iteratively execute each task defined in the workflow plan.
    // This loops sequentially. It naturally continues to the next task if the current one had no manual dependencies.
    for (let i = 0; i < plan.tasks.length; i++) {
      const task = plan.tasks[i];

      // Skip ONLY successfully completed tasks so we can resume naturally.
      // Failed tasks will NOT be skipped, allowing the user to seamlessly re-execute them!
      if (task.status === 'completed') {
        continue;
      }

      allTasksCompleted = false;

      // Mark the individual timeline task as 'in-progress' in the UI optimistically
      this.store.dispatch(
        AiChatActions.setWorkflowTaskStatus({
          messageId,
          taskIndex: i,
          status: 'in-progress',
        }),
      );

      try {
        // Determine the specific AI Function agent to invoke based on the task's semantic name or type.
        // If it's explicitly typed as 'aiFunction', use its assigned name; otherwise, default to the general orchestrator.
        const agentName =
          task.type === 'aiFunction' && task.name
            ? task.name
            : 'executeOrchestratorWorkflowTask';

        // 1. Dispatch an optimistic user message to the UI so the user visibly tracks the active execution step
        const stepUserMessageId = generateTempId(); // CRITICAL FIX: The step message gets its own unique ID, not the plan's message ID!
        this.store.dispatch(
          AiChatActions.addMessage({
            message: {
              id: stepUserMessageId,
              text: task.description, // Sending ONLY the plain text task description as the user message
              isUser: true,
              timestamp: new Date(),
              type: 'workflow-step', // Distinguish auto-generated workflow prompts from normal user-messages
              status: 'sent',
              aiFunctionId: agentName,
              workflowStepData: { stepIndex: i },
            },
          }),
        );
        this.store.dispatch(AiChatActions.setAiThinking({ isThinking: true }));

        // 2. Submit ONLY the plain task description to the specific AI Agent.
        let promptDescription = task.description;

        // CRITICAL FIX: Inject context data into the prompt if the agent is JSONata
        if (agentName === 'RapiderDataTransformationWithJsonata') {
          const sampleContext = generateSampleContext(context);
          promptDescription = buildJsonataTransformationPrompt(
            task.description,
            sampleContext,
            context['__metadata']?.descriptions,
          );
        }

        const payload = buildDirectMessageFormDataPayload(
          promptDescription,
          currentThreadId || '',
          AI_CHAT_CONFIG.constants.aiReceiverId,
          [],
          [],
          agentName,
          'workflow-step', // Distinguish auto-generated workflow prompts from normal user-messages
        );

        // Inject the step index into the __rapider object so it persists in the database
        if (!payload.body.data) payload.body.data = {};
        payload.body.data.__rapider = { workflowStepData: { stepIndex: i } };

        const response: any = await firstValueFrom(
          this.messageController.sendDirectMessageByApiKey$FormData(payload),
        );
        const aiError = extractAiError(response);

        if (aiError) {
          throw new Error(`AI Agent Error: ${aiError}`);
        }

        // Safely extract identifying metadata and artifacts from the backend response
        const realUserMsgId = response.userMessage?.id;
        const realAiMsgId = response.message?.id || response.aiMessage?.id;
        const returnedThreadId =
          response.message?.threadId || (response as any).userMessage?.threadId;
        const aiText = parseAiResponseText(response);
        const entityData = extractEntityData(response);
        const actions = extractAiActions(response);
        const actionsUIConfig = extractAiActionsUIConfig(response);

        // Extract any context specifically returned by the AI
        const aiContext = extractContextFromAiResponse(response);

        // 3. Thread Hydration: Capture the newly generated thread ID from the backend.
        if (!currentThreadId && returnedThreadId) {
          currentThreadId = returnedThreadId as string;

          // CRITICAL REMAPPING: Move the temporary 'transient-thread' context memory into the real thread ID
          this.saveThreadContext(currentThreadId, context);
          this.clearThreadContext('transient-thread');

          this.store.dispatch(
            AiChatActions.setActiveThread({ threadId: currentThreadId }),
          );
        }

        // CRITICAL UPDATE: Merge any context explicitly returned by the AI Response into our persistent memory
        if (aiContext) {
          context = { ...context, ...aiContext };
          this.saveThreadContext(currentThreadId, context);
        }

        // Update the optimistic temporary UI message ID with the real database ID
        if (realUserMsgId) {
          this.store.dispatch(
            AiChatActions.updateMessageId({
              tempId: stepUserMessageId,
              realId: realUserMsgId as string,
            }),
          );
        }

        // STEP VALIDATION & EXECUTION: Evaluate completion based on strict task type rules
        if (agentName === 'RapiderDataTransformationWithJsonata') {
          // CRITICAL FIX: Pass the raw API 'response' payload instead of 'aiText' to successfully
          // extract the natively structured __rapider.jsonataTransformation value.
          const jsonataExpr = extractJsonataExpression(response);

          if (!jsonataExpr) {
            throw new Error(AI_CHAT_CONFIG.texts.jsonataError);
          }

          // Execute the JSONata transformation against the current context
          try {
            const transformedData = await executeJsonataTransformation(
              jsonataExpr,
              context,
            );

            // Save the transformed output back into the context memory.
            // We store it under the 'transformedData' key so the next step (DisplayTheResults)
            // can reliably use {{ transformedData }} in its Handlebars template payloads.
            context['transformedData'] = transformedData;
            this.saveThreadContext(currentThreadId, context);

            console.log(
              '[Workflow Execution Service] JSONata Transformation Successful:',
              transformedData,
            );
          } catch (transformError: any) {
            throw new Error(transformError.message);
          }
        }

        let newMessageId: string | undefined;

        // 4. Dispatch the AI's step resolution (texts, grids, or action cards) to the UI
        if (
          aiText ||
          entityData ||
          (actions && actions.length > 0) ||
          agentName === 'RapiderDataTransformationWithJsonata'
        ) {
          newMessageId = (realAiMsgId as string) ?? generateTempId();
          this.store.dispatch(
            AiChatActions.sendMessageSuccess({
              aiMessage: {
                id: newMessageId,
                type: 'ai-message', // Simplified Type Enforcement
                text: aiText || '',
                isUser: false,
                timestamp: new Date(),
                entityData,
                actions,
                actionsUIConfig,
                jsonataTransformation:
                  agentName === 'RapiderDataTransformationWithJsonata'
                    ? extractJsonataExpression(response) || undefined
                    : undefined,
              },
            }),
          );
        } else {
          // If no payload was returned, simply turn off the loading indicator
          this.store.dispatch(
            AiChatActions.setAiThinking({ isThinking: false }),
          );
        }

        // 5. Evaluate and Execute the retrieved Actions.
        if (actions && actions.length > 0 && newMessageId) {
          // Check if any of the retrieved actions require explicit manual confirmation by the user
          const requiresManualExecution = actions.some(
            (a) => a.payload?.http?.needsConfirmation,
          );

          if (requiresManualExecution) {
            // Auto-Execution Abort: Yield execution flow to the UI.
            console.log(
              '[Workflow Execution Service] Yielding execution to UI for manual user confirmation.',
            );

            this.store.dispatch(
              AiChatActions.setWorkflowTaskStatus({
                messageId,
                taskIndex: i,
                status: 'completed',
              }),
            );

            // Send the completed update info message for this step natively
            await this.sendStatusUpdateMessage(
              task,
              'completed',
              realMessageId,
              i,
              currentThreadId || '',
            );

            // Check if there are more pending tasks. If so, revert the main card to "idle"
            const nextPendingIndex = plan.tasks.findIndex(
              (t, idx) => idx > i && t.status === 'pending',
            );
            if (nextPendingIndex !== -1) {
              this.store.dispatch(
                AiChatActions.setWorkflowExecutionState({
                  messageId,
                  executionState: { status: 'idle' },
                }),
              );
            } else {
              this.store.dispatch(
                AiChatActions.setWorkflowExecutionState({
                  messageId,
                  executionState: { status: 'success' },
                }),
              );
            }

            // Break the sequence to wait for manual user resolution of the pending actions
            return;
          } else {
            // Auto-Execution Proceed: Recursively execute the actions and capture the mutated context
            const result = await this.executeActionsSequentially(
              actions,
              newMessageId,
              currentThreadId,
              i,
              context,
              realUserMsgId as string,
            );
            context = result.context; // Persist context for the next workflow step

            if (!result.success) {
              throw new Error(
                'Action execution failed inside sequential runner',
              );
            }
          }
        }

        // Mark the individual task as completed successfully in the timeline UI
        this.store.dispatch(
          AiChatActions.setWorkflowTaskStatus({
            messageId,
            taskIndex: i,
            status: 'completed',
          }),
        );

        // Step fully completed automatically, send info update message and naturally loop to the next pending step
        await this.sendStatusUpdateMessage(
          task,
          'completed',
          realMessageId,
          i,
          currentThreadId || '',
        );
      } catch (error: any) {
        console.error('❌ [Workflow Execution Service] Task failed:', error);

        // Mark the specific task as failed
        this.store.dispatch(
          AiChatActions.setWorkflowTaskStatus({
            messageId,
            taskIndex: i,
            status: 'failed',
          }),
        );

        // Send the failed update info message for this step natively
        await this.sendStatusUpdateMessage(
          task,
          'failed',
          realMessageId,
          i,
          currentThreadId || '',
        );

        // Mark the overarching workflow plan card as errored
        this.store.dispatch(
          AiChatActions.setWorkflowExecutionState({
            messageId,
            executionState: {
              status: 'error',
              error: `Task failed: ${task.description}`,
            },
          }),
        );
        this.store.dispatch(AiChatActions.setAiThinking({ isThinking: false }));

        // Abort execution of any remaining tasks on critical failure
        return;
      }
    }

    // If the loop completes and all tasks are completely executed, mark the overall plan as a success
    if (
      allTasksCompleted ||
      plan.tasks.every((t) => t.status === 'completed')
    ) {
      this.store.dispatch(
        AiChatActions.setWorkflowExecutionState({
          messageId,
          executionState: { status: 'success' },
        }),
      );
    }
  }

  /**
   * Dispatches a purely informational status update message to the backend and UI.
   * Crucially removes the 'aiFunctionId' and forces the 'type' to 'info' so it does not trigger an orchestration loop.
   * @param {WorkflowTask} task - The specific workflow task.
   * @param {'completed' | 'failed'} status - The outcome status.
   * @param {string} messageId - The REAL database ID of the original message containing the workflow plan.
   * @param {number} taskIndex - The array index of the task.
   * @param {string} threadId - The active chat thread.
   */
  private async sendStatusUpdateMessage(
    task: WorkflowTask,
    status: 'completed' | 'failed',
    messageId: string,
    taskIndex: number,
    threadId: string,
  ): Promise<void> {
    const updateText = `Workflow step ${status}: ${task.name || task.type}`;

    // Pass undefined for aiFunctionId to prevent the SDK builder from appending it
    const updatePayload = buildDirectMessageFormDataPayload(
      updateText,
      threadId,
      AI_CHAT_CONFIG.constants.aiReceiverId,
      [],
      [],
      undefined,
      'action-response', // Simplified Type Enforcement
    );
    delete updatePayload.body.aiFunctionId;

    // Inject the update object payload manually to trigger the reducer rewrite
    if (!updatePayload.body.data) updatePayload.body.data = {};
    updatePayload.body.data.__rapider = {
      workflowPlan: {
        update: {
          originatingStepId: messageId,
          stepIndex: taskIndex,
          status: status,
        },
      },
    };

    try {
      const response = await firstValueFrom(
        this.messageController.sendDirectMessageByApiKey$FormData(
          updatePayload,
        ),
      );
      const updatePlanData = extractWorkflowPlanUpdate(response);

      this.store.dispatch(
        AiChatActions.sendMessageSuccess({
          aiMessage: {
            id:
              response?.aiMessage?.id ||
              response?.message?.id ||
              generateTempId(),
            type: 'action-response', // CRITICAL FIX: Enforce core message types natively
            text: parseAiResponseText(response) || updateText,
            isUser: false,
            timestamp: new Date(),
            workflowPlanUpdate:
              updatePlanData ||
              updatePayload.body.data.__rapider.workflowPlan.update,
          },
        }),
      );
    } catch (e) {
      console.error('Failed to send status update info message to backend', e);
      // Fallback local UI update if network fails
      this.store.dispatch(
        AiChatActions.setWorkflowTaskStatus({ messageId, taskIndex, status }),
      );
    }
  }

  /**
   * Executes a specific array of API actions sequentially (ONE BY ONE).
   * Crucially handles dynamic variable interpolation by compiling the payload against the thread's memory context
   * BEFORE executing each endpoint, and updates the context with the response AFTER executing each endpoint.
   * This ensures that subsequent endpoints in the array can reliably use variables generated by earlier endpoints.
   * * @param {AiAction[]} actions - The array of endpoint action definitions to execute.
   * @param {string} messageId - The UI chat message ID that visually contains the action card (originatedActionMessageId).
   * @param {string | null} activeThreadId - The active chat thread ID used to retrieve context memory and log backend history.
   * @param {number} taskIndex - The index of the current workflow task (used for fallback context keys).
   * @param {Record<string, any>} [initialContext] - An optional context override block.
   * @param {string} [originatingRequestMessageId] - Optional tracking ID of the original request message.
   * @returns {Promise<{context: Record<string, any>, success: boolean}>} A resolution envelope detailing success and the newly mutated context.
   */
  /**
   * Executes a collection of AI-proposed Actions sequentially, updating UI states and reporting results.
   * Supports both client-executed HTTP endpoint actions and server-executed actions (MCP tools).
   * After all actions in the batch are processed, dispatches a structured `AiChatActions.sendMessage`
   * with `data.actionResult` containing the execution outcomes or server confirmation decisions.
   * @param {AiAction[]} actions - The list of actions to execute sequentially.
   * @param {string} messageId - The UI message identifier containing these actions.
   * @param {string | null} activeThreadId - The currently active chat thread ID.
   * @param {number} [taskIndex=0] - The optional task index when called from a workflow plan.
   * @param {Record<string, any>} [initialContext] - Optional initial context variables to seed execution memory.
   * @param {string} [originatingRequestMessageId] - Optional tracking ID of the original user prompt message.
   * @returns {Promise<{ context: Record<string, any>, success: boolean }>} The mutated context and overall success flag.
   */
  public async executeActionsSequentially(
    actions: AiAction[],
    messageId: string,
    activeThreadId: string | null,
    taskIndex: number = 0,
    initialContext?: Record<string, any>,
    originatingRequestMessageId?: string,
  ): Promise<{ context: Record<string, any>; success: boolean }> {
    // Fetch the global persistent context so disconnected manual UI clicks can access previously stored variables
    const context = this.getThreadContext(activeThreadId);

    // Merge any optionally provided initial context from the overarching orchestrator flow
    if (initialContext) {
      Object.assign(context, initialContext);
    }

    // Resolve the real message ID from the store to ensure updates link correctly
    const messages = this.store.selectSignal(selectMessages)();
    const actualMessage = messages.find(
      (m) =>
        m.id === messageId || m.workflowPlan?.originalMessageId === messageId,
    );
    const realMessageId = actualMessage?.id || messageId;

    const actionResults: any[] = [];
    const batchMutations: ModelMutationDescriptor[] = [];
    let allSuccessful = true;

    // Process endpoints strictly ONE BY ONE, awaiting full resolution before proceeding
    for (let actionIndex = 0; actionIndex < actions.length; actionIndex++) {
      const action = actions[actionIndex];

      // A batch can carry actions the user switched off (`isActive: false`, mobile parity)
      const isConfirmed = action.isActive !== false;

      // Handle Server Actions (MCP tools) per Frontend MCP Confirmation Spec
      if (isServerAction(action)) {
        this.store.dispatch(
          AiChatActions.setActionExecutionState({
            messageId,
            actionDescription: action.description,
            actionIndex,
            executionState: { status: 'loading' },
          }),
        );

        // For server actions (MCP tools), report the decision: { status: 'success', confirmed }
        actionResults.push({
          action: action.description,
          actionIndex,
          status: 'success',
          confirmed: isConfirmed,
          result: { confirmed: isConfirmed },
          response: { confirmed: isConfirmed },
        });

        this.store.dispatch(
          AiChatActions.setActionExecutionState({
            messageId,
            actionDescription: action.description,
            actionIndex,
            executionState: {
              status: 'success',
              response: { confirmed: isConfirmed },
            },
          }),
        );
        continue;
      }

      // A switched-off endpoint action is skipped, never executed (mobile parity)
      if (!isConfirmed) {
        actionResults.push({
          action: action.description,
          actionIndex,
          status: 'success',
          confirmed: false,
          result: { confirmed: false },
          response: { confirmed: false },
        });

        this.store.dispatch(
          AiChatActions.setActionExecutionState({
            messageId,
            actionDescription: action.description,
            actionIndex,
            executionState: {
              status: 'success',
              response: { confirmed: false },
            },
          }),
        );
        continue;
      }

      const httpPayload = action.payload?.http;
      if (!httpPayload) continue;

      // 1. Set the visual state of the specific action block to 'loading' in the UI
      this.store.dispatch(
        AiChatActions.setActionExecutionState({
          messageId,
          actionDescription: action.description,
          actionIndex,
          executionState: { status: 'loading' },
        }),
      );

      try {
        // 2. Variable Resolution: Compile the JSON payloads using Handlebars against the memory Context.
        // This injects data (like retrieved IDs) from previous steps into the current API call.
        const resolvedParams = this.resolvePayloadWithHandlebars(
          httpPayload.params,
          context,
        );
        const resolvedBody = this.resolvePayloadWithHandlebars(
          httpPayload.body,
          context,
        );

        let response: any;
        let correlationId: string | undefined = undefined; // Track the header natively

        // 3. Network Execution Logic: Branch based on target environment

        // CRITICAL FIX: Identify if this is a core workflow API that must bypass the project workspace
        const isWorkflowManagementApi =
          httpPayload.path &&
          httpPayload.path.includes('workflow-management/workflow-events/run');
        const isRapiderApi =
          (action.operation && action.operation.endsWith('_onRapider')) ||
          isWorkflowManagementApi;

        if (isRapiderApi) {
          // Route to the internal Rapider management APIs
          const cleanBase = environment.apiBaseUrl.endsWith('/')
            ? environment.apiBaseUrl.slice(0, -1)
            : environment.apiBaseUrl;
          let cleanPath = httpPayload.path.startsWith('/')
            ? httpPayload.path
            : `/${httpPayload.path}`;

          // CRITICAL FIX: Force the path to hit the JWT-authenticated controller endpoints
          if (
            isWorkflowManagementApi &&
            !cleanPath.includes('-with-jwt') &&
            !cleanPath.includes('/logs')
          ) {
            cleanPath = `${cleanPath}-with-jwt`;
          }

          const fullPath = buildPathWithParams(
            `${cleanBase}${cleanPath}`,
            resolvedParams,
          );

          const options: any = { observe: 'response' };

          // CRITICAL FIX: Explicitly fetch and attach the JWT token
          const token =
            localStorage.getItem('Authorization') ||
            localStorage.getItem('accessToken');
          if (token) {
            options.headers = {
              Authorization: token.startsWith('Bearer ')
                ? token
                : `Bearer ${token}`,
            };
          }

          // GET/HEAD requests must not include a body payload
          if (
            resolvedBody &&
            !['GET', 'HEAD'].includes(httpPayload.method.toUpperCase())
          ) {
            options.body = resolvedBody;
          }

          const httpResponse: any = await firstValueFrom(
            this.http.request(httpPayload.method, fullPath, options),
          );

          // Extract the body and the specific workflow correlationId header
          response = httpResponse.body;
          correlationId =
            httpResponse.headers.get('x-correlation-id') ||
            httpResponse.headers.get('X-Correlation-ID') ||
            httpResponse.headers.get('correlationid') ||
            httpResponse.headers.get('correlationId') ||
            undefined;
        } else {
          // Hardcoded flag for proxy path routing
          const useProxyPath = true;

          if (useProxyPath && httpPayload.proxyPath) {
            // Route to Rapider's API + proxyPath
            const cleanBase = environment.apiBaseUrl.endsWith('/')
              ? environment.apiBaseUrl.slice(0, -1)
              : environment.apiBaseUrl;
            const cleanPath = httpPayload.proxyPath.startsWith('/')
              ? httpPayload.proxyPath
              : `/${httpPayload.proxyPath}`;
            const fullPath = buildPathWithParams(
              `${cleanBase}${cleanPath}`,
              resolvedParams,
            );

            const options: any = { observe: 'response' };
            const token =
              localStorage.getItem('Authorization') ||
              localStorage.getItem('accessToken');
            if (token) {
              options.headers = {
                Authorization: token.startsWith('Bearer ')
                  ? token
                  : `Bearer ${token}`,
              };
            }
            if (
              resolvedBody &&
              !['GET', 'HEAD'].includes(httpPayload.method.toUpperCase())
            ) {
              options.body = resolvedBody;
            }

            const httpResponse: any = await firstValueFrom(
              this.http.request(httpPayload.method, fullPath, options),
            );
            response = httpResponse.body;
            correlationId =
              httpResponse.headers.get('x-correlation-id') ||
              httpResponse.headers.get('X-Correlation-ID') ||
              httpResponse.headers.get('correlationid') ||
              httpResponse.headers.get('correlationId') ||
              undefined;
          } else {
            // Route to the user's generated workspace API using the secure ProjectDataService
            const fullPath = buildPathWithParams(
              httpPayload.path,
              resolvedParams,
            );

            let rawResult: any;
            let hasHeaders = false;

            try {
              if (
                typeof (this.projectDataService as any)
                  .executeProjectApiCall$Response === 'function'
              ) {
                rawResult = await firstValueFrom(
                  (
                    this.projectDataService as any
                  ).executeProjectApiCall$Response(
                    httpPayload.method,
                    fullPath,
                    resolvedBody,
                  ),
                );
                hasHeaders = true;
              } else {
                rawResult = await firstValueFrom(
                  (this.projectDataService as any).executeProjectApiCall(
                    httpPayload.method,
                    fullPath,
                    resolvedBody,
                    { observe: 'response' },
                  ),
                );
                if (rawResult && rawResult.headers) hasHeaders = true;
              }
            } catch (e) {
              rawResult = await firstValueFrom(
                this.projectDataService.executeProjectApiCall(
                  httpPayload.method,
                  fullPath,
                  resolvedBody,
                ),
              );
            }

            if (
              hasHeaders &&
              rawResult &&
              rawResult.headers &&
              typeof rawResult.headers.get === 'function'
            ) {
              response = rawResult.body;
              correlationId =
                rawResult.headers.get('x-correlation-id') ||
                rawResult.headers.get('X-Correlation-ID') ||
                rawResult.headers.get('correlationid') ||
                rawResult.headers.get('correlationId') ||
                undefined;
            } else {
              response = rawResult;
            }
          }
        }

        // 4. Context Mutation: Save the API response into the memory dictionary.
        const returnTypeRaw =
          httpPayload.returnType ||
          (httpPayload as any).projectModelName ||
          'Data';
        const returnTypeStr =
          returnTypeRaw.charAt(0).toLowerCase() + returnTypeRaw.slice(1);
        const fallbackKey = `${returnTypeStr}`;

        const contextKey = action.workflowContextVariable || fallbackKey;

        context[contextKey] = response;

        // Store the description of the action in the metadata so the JSONata agent understands the semantic context of the data
        context['__metadata'] = context['__metadata'] || { descriptions: {} };
        context['__metadata'].descriptions[contextKey] = action.description;

        // Save context back into persistent map and storage
        this.saveThreadContext(activeThreadId, context);

        // 5. Update the visual state of the action block to 'success' in the UI
        this.store.dispatch(
          AiChatActions.setActionExecutionState({
            messageId,
            actionDescription: action.description,
            actionIndex,
            executionState: {
              status: 'success',
              response,
              correlationId,
            },
          }),
        );

        // Wake up NgRx Effect to start polling if correlationId exists
        if (correlationId) {
          this.store.dispatch(
            AiChatActions.startWorkflowPolling({
              messageId: messageId,
              correlationId: correlationId,
            }),
          );
        }

        // Add to structured action results per turn input contract
        actionResults.push({
          action: action.description,
          actionIndex,
          status: 'success',
          statusCode: response === null ? 204 : 200,
          result:
            response === null ||
            (typeof response === 'string' && response.trim() === '')
              ? correlationId
                ? { correlationId, accepted: true }
                : {}
              : correlationId
                ? { ...response, correlationId }
                : response,
          response,
          correlationId,
        });

        // Data Insights: Push the mutation event to the global timeline and navigate canvas focus
        const successLogEvent = buildDataOperationEventPayload(
          action,
          true,
          response,
        );
        this.store.dispatch(
          AiChatActions.logDataOperation({ event: successLogEvent }),
        );
        this.store.dispatch(
          ProjectDataActions.setActiveInsight({ insight: successLogEvent }),
        );

        const upperMethod = (httpPayload.method || '').toUpperCase();
        if (
          !isWorkflowManagementApi &&
          ['POST', 'PUT', 'PATCH', 'DELETE'].includes(upperMethod) &&
          successLogEvent.operationType !== 'READ'
        ) {
          batchMutations.push({
            operationType: successLogEvent.operationType,
            httpMethod: upperMethod,
            projectModelName: successLogEvent.projectModelName,
            projectModelId: successLogEvent.projectModelId,
            dataTableName: successLogEvent.dataTableName,
            endpointPath:
              successLogEvent.endpointPath ||
              httpPayload.proxyPath ||
              httpPayload.path,
            recordIds: successLogEvent.recordIds,
          });
        }
      } catch (error: any) {
        console.error(
          '❌ [Workflow Execution Service] Execution failed:',
          error,
        );
        allSuccessful = false;

        // Standardize the error text for presentation and logging
        const fallbackErrorText =
          error?.error?.message || error?.message || 'Execution failed.';
        const errorStatus = error?.status || 500;

        // Update the visual state of the action block to 'error' in the UI
        this.store.dispatch(
          AiChatActions.setActionExecutionState({
            messageId,
            actionDescription: action.description,
            actionIndex,
            executionState: { status: 'error', error: fallbackErrorText },
          }),
        );

        actionResults.push({
          action: action.description,
          actionIndex,
          status: 'error',
          statusCode: errorStatus,
          error: fallbackErrorText,
          result: {
            statusCode: errorStatus,
            error: {
              statusCode: errorStatus,
              message: fallbackErrorText,
            },
          },
        });

        // Data Insights: Push the failure event to the global timeline
        const errorLogEvent = buildDataOperationEventPayload(
          action,
          false,
          fallbackErrorText,
        );
        this.store.dispatch(
          AiChatActions.logDataOperation({ event: errorLogEvent }),
        );

        // Stop sequential execution on error
        break;
      }
    }

    // Invalidate SWR cache for mutated project models and notify open canvas views once per batch
    if (batchMutations.length > 0) {
      const workspaceModels =
        this.store.selectSignal(selectProjectModelsList)() || [];
      const { rawEntityTokens } = resolveMutationModelTokens(
        batchMutations,
        workspaceModels,
      );
      clearSwrSessionCacheForEntities(rawEntityTokens);
      this.store.dispatch(
        ProjectDataActions.notifyModelMutations({ mutations: batchMutations }),
      );
    }

    // 6. Report outcomes back to the AI agent runtime via structured turn-input contract
    if (actionResults.length > 0) {
      const successCount = actionResults.filter(
        (r) => r.status === 'success',
      ).length;
      const errorCount = actionResults.length - successCount;
      const summaryText =
        actions.length === 1
          ? isServerAction(actions[0])
            ? actions[0].description || 'Action confirmed'
            : actions[0].description || 'Action executed'
          : `Actions executed: ${successCount} succeeded${errorCount > 0 ? `, ${errorCount} failed` : ''}`;

      const targetActionIndex = actions.length === 1 ? 0 : undefined;
      this.store.dispatch(
        AiChatActions.sendMessage({
          text: summaryText,
          messageType: 'user-message',
          isConfirmation: false,
          isShownInChat: false,
          isHidden: true,
          data: {
            actionResult: actionResults,
            originatingRequestMessageId,
            originatedActionMessageId: realMessageId,
            actionIndex: targetActionIndex,
            isShownInChat: false,
            isHidden: true,
          },
        }),
      );
    }

    // Return the mutated context so the caller can persist the chained variables
    return { context, success: allSuccessful };
  }

  /**
   * Resolves Handlebars tags (`{{ variable }}`) inside JSON payload parameters by rendering them
   * against an active memory context dictionary.
   * Uses `noEscape: true` to prevent Handlebars from HTML-encoding values, ensuring strictly
   * formatted JSON strings don't break.
   * @param {any} payload - The raw JSON payload (body or params) potentially containing Handlebars tags.
   * @param {Record<string, any>} context - The dictionary of variables used for replacement.
   * @returns {any} The parsed and resolved payload ready for transmission.
   */
  private resolvePayloadWithHandlebars(
    payload: any,
    context: Record<string, any>,
  ): any {
    if (!payload) return payload;
    if (Object.keys(context).length === 0) return payload; // Skip processing to save execution cycles if context is empty

    try {
      // Stringify the object to parse it globally using Handlebars
      const str = JSON.stringify(payload);

      // Compile the string into a template function without HTML escaping
      const template = Handlebars.compile(str, {
        noEscape: true,
        strict: false,
      });

      // Execute template replacement
      const resolvedStr = template(context);

      // Parse it back into a standard object
      return JSON.parse(resolvedStr);
    } catch (e) {
      console.warn(
        '⚠️ [Workflow Execution Service] Handlebars resolution failed, falling back to raw payload',
        e,
      );
      return payload; // Fallback to raw payload to prevent critical failure if resolution throws
    }
  }

  /**
   * Dispatches a cancellation decision for a server-executed action (MCP tool) to the backend agent runtime.
   * Reports { status: 'success', confirmed: false } in data.actionResult[] per spec §3.2.
   * @param {AiAction} action - The server action that was cancelled.
   * @param {string} messageId - The UI chat message ID.
   * @param {string | null} activeThreadId - The active chat thread ID.
   * @param {number} [actionIndex=0] - The index of the action in the actions array.
   * @param {string} [originatingRequestMessageId] - Optional tracking ID of the original request.
   * @returns {Promise<void>}
   */
  public async cancelServerAction(
    action: AiAction,
    messageId: string,
    activeThreadId: string | null,
    actionIndex: number = 0,
    originatingRequestMessageId?: string,
  ): Promise<void> {
    try {
      const messages = this.store.selectSignal(selectMessages)();
      const actualMessage = messages.find(
        (m) =>
          m.id === messageId || m.workflowPlan?.originalMessageId === messageId,
      );
      const realMessageId = actualMessage?.id || messageId;

      // Immediately set the action executionState to success with confirmed: false in local store
      this.store.dispatch(
        AiChatActions.setActionExecutionState({
          messageId: realMessageId,
          actionDescription: action.description,
          executionState: {
            status: 'success',
            response: { confirmed: false },
          },
        }),
      );

      this.store.dispatch(
        AiChatActions.sendMessage({
          text: `Action cancelled.`,
          messageType: 'user-message',
          isConfirmation: false,
          isShownInChat: false,
          isHidden: true,
          data: {
            actionResult: [
              {
                action: action.description,
                actionIndex,
                status: 'success',
                confirmed: false,
                result: { confirmed: false },
                response: { confirmed: false },
              },
            ],
            originatingRequestMessageId,
            originatedActionMessageId: realMessageId,
            actionIndex,
            isShownInChat: false,
            isHidden: true,
          },
        }),
      );
    } catch (error) {
      console.error(
        '❌ [Workflow Execution Service] Failed to send server action cancellation:',
        error,
      );
    }
  }
}
